'use strict';

/**
 * UpiModule — UPI/QR collection operations.
 *
 * Most calls go through fintech-gateway's wildcard proxy at
 * POST/GET /process/merchant/payment/<path>, which forwards verbatim to
 * paymentSystem's /merchant/payment/<path> (see
 * src/modules/merchant/routes/payments.routes.js, mounted at
 * /merchant/payment). QR generation is the one exception — see generateQr().
 */
class UpiModule {
  constructor(client) {
    this._c = client;
  }

  _post(path, body = {}) { return this._c.post(`merchant/payment/${path}`, body); }
  _get(path, body = {})  { return this._c.get(`merchant/payment/${path}`, body); }

  /**
   * Generates a UPI QR — one endpoint for both static and dynamic, matching
   * payapp's actual flow (composables/apis/usepaymentsApi.ts's StaticQR/
   * DynamicQR both POST here) and its backend contract, documented verbatim
   * in isg.txn.controller.js#ISGgenerateQR:
   *   { merchantId: "" // optional, vendor clientId only
   *     amount: ""     // optional for StaticQR / required for DynamicQR
   *     order_id: ""   // optional for both — auto-generated server-side if omitted }
   * Omit `amount` for a static QR (the terminal's fixed VPA, no amount
   * encoded); include it for a dynamic, amount-specific QR. Response's
   * `data.qr_image` is a ready base64 PNG data URL — no client-side QR
   * rendering needed. Mounted at /upi/txn/v1 (see isg.txt.js), not
   * /merchant/payment like the rest of this module.
   * @param {{ amount?: number|string, order_id?: string, merchantId?: string }} [body]
   */
  generateQr(body = {})           { return this._c.post('upi/txn/v1/GenerateQR', body); }

  /** @deprecated alias for generateQr — same endpoint now handles both. */
  dynamicQr(body)                 { return this.generateQr(body); }

  /**
   * Collect request pushed straight to the customer's VPA (no QR shown).
   * @param {{ amount: number, order_id: string, vpa: string, customer_mobile?: string,
   *            customer_name?: string, customer_email?: string, remarks?: string }} body
   */
  collect(body)                   { return this._post('Collection', body); }

  /**
   * Payment link sent by SMS to the customer's mobile.
   * @param {{ amount: number, order_id: string, customer_number: string,
   *            customer_name?: string, remarks?: string }} body
   */
  smsPay(body)                    { return this._post('SMSpay', body); }

  /**
   * Checks a UPI QR/collect transaction's status. Moved off
   * /merchant/payment/UpiStatus onto /upi/txn/v1 (see isg.txt.js) alongside
   * generateQr — same merchant-resolution pattern (merchantId optional,
   * vendor clientId only).
   * @param {{ trans_id: string, merchantId?: string }} body
   */
  upiStatus(body)                 { return this._c.post('upi/txn/v1/UpiStatus', body); }

  /** @param {{ trans_id: string }} body */
  smsStatus(body)                 { return this._post('SMSStatus', body); }

  /** @param {{ tr: string, amount: number }} body */
  refundUpi(body)                 { return this._post('RefundUPI', body); }

  /** @param {{ trans_id: string }} body */
  refundSms(body)                 { return this._post('RefundSMS', body); }

  /** @param {{ payer_virtual_address: string }} body */
  verifyVpa(body)                 { return this._post('verifyVPA', body); }

  /** @param {{ page?: number, limit?: number|string, startDate?: string, endDate?: string,
   *            status?: string, paymentMethod?: string }} [query] */
  transactions(query = {})        { return this._get('transactions', query); }

  /** Success/failed/pending counts for the dashboard summary chart. */
  transactionStatusSummary()      { return this._get('transactions/status-summary'); }

  /** @param {{ granularity: 'day'|'week'|'month'|'year'|'5years'|'10years' }} body */
  collectionsByGranularity(body)  { return this._post('getCollectionsByGranularity', body); }
}

module.exports = { UpiModule };
